import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { EnrichedPtuData } from '@/features/bidding/hooks/useBidOfferEnrichedPtus'
import type { OfferedAndForecastAndSuggestedPtu } from '@/features/bidding/types/bid'

function arePtuMapsEqual(
  a: Map<string, OfferedAndForecastAndSuggestedPtu[]>,
  b: Map<string, OfferedAndForecastAndSuggestedPtu[]>,
): boolean {
  if (a.size !== b.size) return false

  for (const [key, aValue] of a) {
    const bValue = b.get(key)
    if (!bValue || aValue.length !== bValue.length) return false

    for (let i = 0; i < aValue.length; i++) {
      const aPtu = aValue[i]
      const bPtu = bValue[i]

      if (
        !aPtu.ptu.start.equals(bPtu.ptu.start) ||
        !aPtu.ptu.end.equals(bPtu.ptu.end) ||
        aPtu.offeredVolume?.quantity !== bPtu.offeredVolume?.quantity ||
        aPtu.offeredVolume?.unit !== bPtu.offeredVolume?.unit ||
        aPtu.forecastedVolume?.quantity !== bPtu.forecastedVolume?.quantity ||
        aPtu.forecastedVolume?.unit !== bPtu.forecastedVolume?.unit ||
        aPtu.suggestedVolume?.quantity !== bPtu.suggestedVolume?.quantity ||
        aPtu.suggestedVolume?.unit !== bPtu.suggestedVolume?.unit
      ) {
        return false
      }
    }
  }
  return true
}

export type UseBidOfferCurrentPtusResult = {
  currentPtusByActivationGroup: Map<string, OfferedAndForecastAndSuggestedPtu[]>
  updatePtusForActivationGroup: (activationGroupUuid: string, ptus: OfferedAndForecastAndSuggestedPtu[]) => void
  updateSinglePtu: (activationGroupUuid: string, newPtu: OfferedAndForecastAndSuggestedPtu) => void
}

/**
 * Hook to manage the current (edited) PTU state for all activation groups.
 * This hook maintains the form state that users can edit, separate from the initial values.
 *
 * The current PTUs start as copies of the initialPtus from enrichedPtusByActivationGroup,
 * but can be updated independently as users make changes in the forms.
 *
 * This separation is critical for the "Save All" functionality - it allows saving
 * the current form values (with all the computed values) rather than the initial values.
 *
 * The state is initialized when the component mounts and automatically updated when
 * enrichedPtusByActivationGroup loads or changes. New activation groups are added to the state,
 * but existing ones are preserved to maintain user edits. When users navigate to a different
 * day/page, the component will unmount and remount with fresh state from the new enrichedData.
 *
 * @param enrichedPtusByActivationGroup - Map of enriched PTU data by activationGroupUuid
 * @returns Object containing:
 *  - currentPtusByActivationGroup: Map of current PTU state by activationGroupUuid
 *  - updatePtusForActivationGroup: Callback to update PTUs for a specific activation group
 *
 * @example
 * ```tsx
 * const { currentPtusByActivationGroup, updatePtusForActivationGroup } = useBidOfferCurrentPtus(
 *   enrichedPtusByActivationGroup
 * )
 *
 * // Get current PTUs for an activation group
 * const currentPtus = currentPtusByActivationGroup.get(activationGroupUuid)
 *
 * // Update PTUs when user makes changes
 * updatePtusForActivationGroup(activationGroupUuid, newPtus)
 * ```
 */
export function useBidOfferCurrentPtus(
  enrichedPtusByActivationGroup: Map<string, EnrichedPtuData>,
): UseBidOfferCurrentPtusResult {
  const userEditedGroupsRef = useRef<Set<string>>(new Set())

  // Initialize state Map with initial PTU values from enrichedData
  // This only runs once when the component mounts (lazy initialization)
  const [currentPtusByActivationGroup, setCurrentPtusByActivationGroup] = useState<
    Map<string, OfferedAndForecastAndSuggestedPtu[]>
  >(() => {
    const map = new Map<string, OfferedAndForecastAndSuggestedPtu[]>()

    for (const [activationGroupUuid, enrichedData] of enrichedPtusByActivationGroup.entries()) {
      // Use initialPtus as the starting point for current state
      map.set(activationGroupUuid, enrichedData.initialPtus)
    }

    return map
  })

  // Update state when enrichedPtusByActivationGroup loads or changes
  // This ensures that if the hook is called before data is loaded (with empty map),
  // the state gets populated once the data arrives
  useEffect(() => {
    setCurrentPtusByActivationGroup((prevMap) => {
      // Start with all existing entries (groups are never removed, only added)
      const newMap = new Map(prevMap)

      for (const [activationGroupUuid, enrichedData] of enrichedPtusByActivationGroup.entries()) {
        const isUserEdited = userEditedGroupsRef.current.has(activationGroupUuid) && prevMap.has(activationGroupUuid)
        if (!isUserEdited) {
          // Use the latest initialPtus (this handles async data loading)
          newMap.set(activationGroupUuid, enrichedData.initialPtus)
        }
      }

      if (arePtuMapsEqual(prevMap, newMap)) {
        return prevMap
      }
      return newMap
    })
  }, [enrichedPtusByActivationGroup])

  // Callback to update PTUs for a specific activation group
  const updatePtusForActivationGroup = useCallback(
    (activationGroupUuid: string, ptus: OfferedAndForecastAndSuggestedPtu[]) => {
      // Mark this group as edited by user to preserve it on future data updates
      userEditedGroupsRef.current.add(activationGroupUuid)

      setCurrentPtusByActivationGroup((prevMap) => {
        const newMap = new Map(prevMap)
        newMap.set(activationGroupUuid, ptus)
        return newMap
      })
    },
    [],
  )

  // Callback to update a single PTU within an activation group
  // Finds the PTU by start time and updates its offeredVolume
  const updateSinglePtu = useCallback((activationGroupUuid: string, newPtu: OfferedAndForecastAndSuggestedPtu) => {
    // Mark this group as edited by user to preserve it on future data updates
    userEditedGroupsRef.current.add(activationGroupUuid)

    setCurrentPtusByActivationGroup((prevMap) => {
      const existingPtus = prevMap.get(activationGroupUuid)
      if (!existingPtus) return prevMap

      const updatedPtus = existingPtus.map((ptu) =>
        ptu.ptu.start.equals(newPtu.ptu.start) ? { ...ptu, offeredVolume: newPtu.offeredVolume } : ptu,
      )

      const newMap = new Map(prevMap)
      newMap.set(activationGroupUuid, updatedPtus)
      return newMap
    })
  }, [])

  // Memoize the return object to prevent unnecessary recalculations in dependent hooks
  return useMemo(
    () => ({
      currentPtusByActivationGroup,
      updatePtusForActivationGroup,
      updateSinglePtu,
    }),
    [currentPtusByActivationGroup, updatePtusForActivationGroup, updateSinglePtu],
  )
}
